import { ArrowForward, Close, ErrorOutlined } from "@mui/icons-material";
import BackIcon from "@mui/icons-material/ArrowBack";
import { Avatar, IconButton, Dialog, Button } from "@mui/material";
import cx from "classnames";
import { Dictionary } from "lodash";
import React, { useEffect, useState, useMemo, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ReactComponent as AddUserIcon } from "assets/add-user.svg";
import { ReactComponent as CrownIcon } from "assets/crown.svg";
import { ReactComponent as FolderHeartPurple } from "assets/folder-heart-purple.svg";
import DotElastic from "components/DotElastic";
import Shot, { ShotRef } from "components/Shot";
import Usage from "components/Usage/Usage";
import {
  PLANS,
  SERVICES,
  UserGuides,
  WINDOW_EVENTS,
} from "constants/constants";
import { UserContext } from "layouts/SlapshotMainLayout";
import { Subscription } from "models/User";
import { getSubscription } from "services/UserService";

import styles from "./ProjectPage.module.scss";
import Shots from "../../components/Shots";
import {
  SLAPSHOT_HOME_URL,
  SLAPSHOT_INVITE_USER,
  SLAPSHOT_MY_PLAN_UPGRADE_URL,
  SLAPSHOT_SUBSCRIPTION_UPGRADE_URL,
} from "../../constants/urls";
import { Project } from "../../models/Project";
import { Shot as ShotType, ShotCompact } from "../../models/Shot";
import { getProject, getProjectId } from "../../services/ProjectService";
import {
  ACTION_SUBSCRIBE_SHOT_UPDATE,
  ACTION_SUBSCRIBE_INFERENCE_UPDATE,
  getRequestData,
  getSocket,
  closeSocket,
  ACTION_SUBSCRIBE_SUBSCRIPTION_UPDATE,
} from "../../services/WebSocketService";
import {
  formatDate,
  getTextInitials,
  getUserNameText,
} from "../../utils/helper";

const ProjectPage = () => {
  const navigate = useNavigate();
  const { projectId, layerId } = useParams();
  const user = useContext(UserContext);

  const [project, setProject] = useState<Project | null>(null);
  const [isProjectLoading, setIsProjectLoading] = useState(true);
  const [isShotsLoading, setIsShotsLoading] = useState(true);

  const [shots, setShots] = useState<Dictionary<ShotCompact[]>>({});
  const [selectedShot, setSelectedShot] = useState<ShotType | null>(null);
  const shotRef = useRef<ShotRef>(null);
  const [service, setService] = useState(SERVICES.ROTOSCOPING);

  const [subscription, setSubscription] = useState<Subscription | null>(null);

  const subscriptionSocket = useMemo(() => {
    if (subscription?.id) return getSocket("subscription");
    return null;
  }, [subscription?.id]);

  // Subscribing to subscription updates
  useEffect(() => {
    if (subscriptionSocket && subscription)
      subscriptionSocket.onopen = (event) => {
        subscriptionSocket.send(
          getRequestData(ACTION_SUBSCRIBE_SUBSCRIPTION_UPDATE, {
            subscription_id: subscription.id,
          }),
        );
      };
  }, [subscription, subscriptionSocket]);

  useEffect(() => {
    if (subscriptionSocket)
      subscriptionSocket.onmessage = (event) => {
        const { data } = JSON.parse(event.data);
        setSubscription(data);
      };

    return () => {
      subscriptionSocket?.close();
    };
  }, [subscriptionSocket]);

  useEffect(() => {
    if (!subscription) return () => {};
    window.dispatchEvent(
      new CustomEvent(WINDOW_EVENTS.SHOW_PLAN_TAG, {
        detail: { plan: subscription.plan.name },
      }),
    );

    return () => {
      window.dispatchEvent(new CustomEvent(WINDOW_EVENTS.HIDE_PLAN_TAG));
    };
  });

  useEffect(() => {
    if (!project) return;
    getSubscription(project.company.id).then((data) => {
      setSubscription(data);
    });
  }, [project]);

  const inferenceSocket = useMemo(() => {
    if (!projectId) return null;
    return getSocket("inference-cutout");
  }, [projectId]);

  // Subscribing to shot updates
  useEffect(() => {
    if (!projectId) return () => {};

    const socket = getSocket("shot");
    if (!socket) {
      return () => {};
    }
    socket.onopen = (event) => {
      socket.send(
        getRequestData(ACTION_SUBSCRIBE_SHOT_UPDATE, {
          project_id: +projectId,
        }),
      );
    };

    const updateShots = (e) => {
      setShots((currentShots) => {
        const newShots = { ...currentShots };
        const key = formatDate(e.created);
        if (newShots[key]) {
          const shotIndex = newShots[key].findIndex((shot) => shot.id === e.id);
          if (shotIndex !== -1) {
            newShots[key][shotIndex] = e;
          }
        }
        return newShots;
      });
    };

    socket.onmessage = (event) => {
      const e = JSON.parse(event.data).data;
      updateShots(e);
    };

    return () => {
      closeSocket("shot");
    };
  }, [projectId]);

  // Subscribing to inference updates
  useEffect(() => {
    if (!projectId) return () => {};

    const socket = getSocket("inference-cutout");
    if (!socket) {
      return () => {};
    }

    socket.onopen = (event) => {
      socket.send(
        getRequestData(ACTION_SUBSCRIBE_INFERENCE_UPDATE, {
          project_id: +projectId,
        }),
      );
    };

    socket.onmessage = (event) => {
      const e = JSON.parse(event.data).data;
      setShots((currentShots) => {
        const newShots = { ...currentShots };
        const key = formatDate(e.shot.created);
        if (newShots[key]) {
          const shotIndex = newShots[key].findIndex(
            (shot) => shot.id === e.shot.id,
          );
          if (shotIndex !== -1) {
            newShots[key][shotIndex] = e.shot;
          }
        }
        return newShots;
      });

      shotRef.current?.onInferenceLayerUpdate(e.layer_id);
    };

    return () => {
      closeSocket("inference-cutout");
    };
  }, [projectId]);

  // Loading selected project...
  useEffect(() => {
    if (projectId) {
      if (Number.isNaN(+projectId)) {
        getProjectId(projectId).then((res) => {
          navigate(`/projects/${res.project_id}`);
        });
      } else {
        getProject(+projectId)
          .then((result) => {
            setProject(result);
          })
          .finally(() => {
            setIsProjectLoading(false);
          });
      }
    }
  }, [projectId, navigate]);

  return (
    <div className={cx(styles.mainContainer)}>
      {isProjectLoading ||
        (isShotsLoading && (
          <div className={styles.projectLoader}>
            <h4>Loading your project...</h4>
            <DotElastic />
          </div>
        ))}

      {!isProjectLoading && project && (
        <div className={isShotsLoading ? styles.hidden : ""}>
          <div className={styles.navRow}>
            <div className={styles.startNav}>
              <IconButton
                className={styles.noBackgroundButtonSmall}
                onClick={() => {
                  navigate(SLAPSHOT_HOME_URL);
                }}
              >
                <BackIcon />
              </IconButton>

              <div className={styles.projectDetails}>
                <FolderHeartPurple />
                <div
                  onClick={() => {
                    navigate(
                      `${SLAPSHOT_HOME_URL}projects/${project.id}/${service}`,
                    );
                  }}
                  className={cx(styles.projectNameBold, [
                    selectedShot && styles.greyProjectName,
                  ])}
                >
                  {project.name}
                </div>

                {selectedShot && (
                  <>
                    <div
                      style={{ color: "#FFFFFF80" }}
                      className={styles.projectNameBold}
                    >
                      |
                    </div>
                    <div
                      className={styles.projectNameBold}
                      onClick={() => {
                        navigate(
                          `${SLAPSHOT_HOME_URL}projects/${project.id}/${service}/shots/${selectedShot.id}`,
                        );
                      }}
                    >
                      {selectedShot.name}
                    </div>
                  </>
                )}

                {subscription?.is_free_trial_period &&
                  subscription.free_trial_period_end_date &&
                  subscription.plan.name === PLANS.FREE &&
                  project?.company.is_admin &&
                  user?.preferences !== undefined &&
                  user.preferences.tips?.includes(UserGuides.FreeTrial) && (
                    <div className={styles.fpbContainer}>
                      <CrownIcon className={styles.fpbIcon} />
                      <div className={styles.fpbText}>
                        Free trial ends on{" "}
                        {formatDate(subscription.free_trial_period_end_date)}
                      </div>
                      <div
                        className={styles.fpbButton}
                        onClick={() => {
                          navigate(
                            project.company.is_owner
                              ? SLAPSHOT_MY_PLAN_UPGRADE_URL
                              : `${SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                          );
                        }}
                      >
                        Upgrade Now
                      </div>
                    </div>
                  )}
              </div>
            </div>
            <div className={styles.right}>
              <div className={styles.projectUsers}>
                {project.users.slice(0, 3).map((u, index) =>
                  u.profile_picture_url ? (
                    <img
                      key={u.id}
                      className={styles.projectUser}
                      src={u.profile_picture_url}
                      alt={u.profile_picture_url}
                      style={{
                        marginRight: "-10px",
                        zIndex: index,
                        border: "2px solid #27273A",
                      }}
                    />
                  ) : (
                    <Avatar
                      key={u.id}
                      className={styles.projectUserAvatar}
                      {...{ children: getTextInitials(u.name) }}
                      sx={{ bgcolor: "purple", width: 28, height: 28 }}
                      style={{
                        marginRight: "-10px",
                        zIndex: index,
                        border: "2px solid #27273A",
                        textAlign: "center",
                      }}
                    />
                  ),
                )}
                {project.users.length > 3 && (
                  <div
                    className={styles.userCount}
                  >{`+${project.users.length - 3}`}</div>
                )}
                <span>
                  {project.users.map((m, index) => (
                    <React.Fragment key={index}>
                      {getUserNameText(m)}
                      <br />
                    </React.Fragment>
                  ))}
                </span>
              </div>
              {project.workspace && project.workspace.is_admin && (
                <button
                  type="button"
                  className={cx("primaryButton", styles.shareButton)}
                  onClick={() => {
                    if (project.workspace) {
                      navigate(
                        `${SLAPSHOT_INVITE_USER}?workspaceId=${project.workspace?.id}&projectIds=${project?.id}`,
                      );
                    }
                  }}
                >
                  <AddUserIcon /> Share
                </button>
              )}

              <Usage subscription={subscription} project={project} />
            </div>
          </div>

          <div className={styles.contentContainer}>
            <div className={styles.shotsContainer}>
              <Shots
                shots={shots}
                setShots={setShots}
                project={project}
                service={service}
                isShotsLoading={isShotsLoading}
                setIsShotsLoading={setIsShotsLoading}
                subscription={subscription}
              />
            </div>

            <Shot
              ref={shotRef}
              project={project}
              selectedShot={selectedShot}
              setSelectedShot={setSelectedShot}
              setShots={setShots}
              service={service}
              setService={setService}
              subscription={subscription}
            />
          </div>
        </div>
      )}

      {subscription?.is_free_trial_period &&
        subscription.free_trial_period_end_date &&
        subscription.plan.name === PLANS.FREE &&
        project?.company.is_admin &&
        user?.preferences !== undefined &&
        !user.preferences.tips?.includes(UserGuides.FreeTrial) && (
          <div className={styles.fpContainer}>
            <CrownIcon className={styles.fpIcon} />
            <div className={styles.fpContent}>
              <div className={styles.fpHeader}>
                <div className={styles.fpTitle}>Trial ending soon...</div>
                <Close
                  className={styles.fpClose}
                  onClick={() => {
                    user?.setPreferences("tips", UserGuides.FreeTrial, true);
                  }}
                />
              </div>
              <div className={styles.fpBody}>
                <span className={styles.fpBodyLight}>
                  The Free trial period is ending on:
                </span>
                <span className={styles.fpBodyBold}>
                  {formatDate(subscription.free_trial_period_end_date)}
                </span>
              </div>
              <div
                className={styles.fpButton}
                onClick={() => {
                  navigate(
                    project.company.is_owner
                      ? SLAPSHOT_MY_PLAN_UPGRADE_URL
                      : `${SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                  );
                }}
              >
                Upgrade Now <ArrowForward />
              </div>
            </div>
          </div>
        )}

      <Dialog
        open={!!subscription && !subscription.renewal_date}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        <div className="modalContainer">
          <div className="modalHeader">
            <h3 className="modalTitle">
              <div className={styles.rmHeader}>Renewal Failed</div>
            </h3>
          </div>
          <div className={styles.rmBody}>
            <div className={styles.rmAttention}>
              <ErrorOutlined />
            </div>
            <div>
              Your plan has <strong>expired</strong> due to payment failure :(
              Please renew to continue your access.
            </div>
          </div>
          <div className="modalActions">
            {project?.company.is_admin && (
              <Button
                type="button"
                className={styles.slapShotButton}
                onClick={() => {
                  navigate(
                    project.company.is_owner
                      ? SLAPSHOT_MY_PLAN_UPGRADE_URL
                      : `${SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                  );
                }}
              >
                Renew
              </Button>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProjectPage;
